// Точка входа виджета. Вставка на Тильду (T9): <div id="okb"></div> + <script src=".../widget.js" defer>.
// apiBase выводится из адреса самого скрипта — виджет работает с того сервера, откуда загружен.
import cssText from "./styles.css";
import { fontFaceCss } from "./fonts.js";
import { createApi } from "./api.js";
import { createState } from "./state.js";
import {
  indexMap,
  candidatesFromLengths,
  cardsFromLengths,
  monthIndexForCandidates,
} from "./lib/avail.js";
import { todayIso, diffNights } from "./lib/dates.js";
import {
  lengthsKey,
  hasNight,
  runSearch,
  isRulesRefusal,
  refusedSearchData,
} from "./lib/search.js";
import {
  validateGuestForm,
  buildBookPayload,
  formSubmitDelay,
  restAtCheckin,
  statusParamsFromLocation,
  bookFingerprint,
  nextBookAttempt,
  keepBookAttempt,
  bookWithRetry,
  bookWaitStage,
} from "./lib/checkout.js";
import { formulaLabel, priceLabel } from "./lib/services.js";
import {
  outCandidates,
  blockedTap,
  resolvePending,
  pickOutEvent,
  moveCheckin,
  calEventParams,
  PENDING_NOTE_DELAY_MS,
} from "./lib/datepick.js";
import { fmtRub } from "./lib/money.js";
import { renderSearch, renderIntro } from "./ui/searchbar.js";
import { scaleFor } from "./lib/scale.js";
import { renderResults } from "./ui/cards.js";
import { renderNotice } from "./ui/notice.js";
import { renderDatesModal, calendarMonths } from "./ui/calendar.js";
import { renderGuestsModal } from "./ui/guests.js";
import { renderCheckout } from "./ui/checkout.js";
import { renderStatus } from "./ui/status.js";
import { GOALS, createTracker } from "./lib/metrika.js";
import { keyFromLocation, gateAllows } from "./lib/gate.js";
import { searchFromLocation, bookingPageUrl } from "./lib/handoff.js";
import { ensureCounter, sendGoal } from "./metrika.js";

// src скрипта захватываем сразу: в ветке DOMContentLoaded (см. низ файла) boot() выполняется
// уже вне скрипта и document.currentScript там null — apiBase уехал бы на домен Тильды
const SCRIPT_SRC = document.currentScript?.src ?? null;

function baseUrl() {
  try {
    return SCRIPT_SRC ? new URL(".", SCRIPT_SRC).href : `${location.origin}/`;
  } catch {
    return `${location.origin}/`;
  }
}

function boot() {
  const root = document.getElementById("okb");
  if (!root) {
    console.warn("okb: контейнер #okb не найден — виджет не смонтирован");
    return;
  }
  const base = baseUrl();
  const api = createApi(`${base}api`);

  // Мини-модуль на главной (T18): контейнер с data-okb-mini="<адрес страницы полного модуля>" —
  // только строка поиска, «Найти» уводит на ту страницу с датами и гостями в query.
  // Пустой атрибут — не мини: new URL("") вёл бы на саму главную
  const miniPage = root.getAttribute("data-okb-mini")?.trim() || null;
  const mini = miniPage != null;
  if (root.hasAttribute("data-okb-mini") && !mini)
    console.warn(
      "okb: data-okb-mini пустой — адрес страницы модуля не задан, показываю полный модуль",
    );
  root.classList.toggle("okb-mini", mini);

  const style = document.createElement("style");
  style.textContent = `${fontFaceCss(`${base}assets/`)}\n${cssText}`;
  document.head.appendChild(style);

  root.innerHTML = `
    <div class="okb-layout">
      <div class="okb-intro" data-okb-region="intro"></div>
      <div class="okb-main">
        <div data-okb-region="search"></div>
        <div data-okb-region="notice"></div>
        <div data-okb-region="results"></div>
      </div>
    </div>
    <div class="okb-overlay" data-okb-region="modal" hidden></div>
    <div class="okb-toast" role="status"></div>`;
  root.dataset.okbReady = "1";

  const regions = {};
  for (const el of root.querySelectorAll("[data-okb-region]")) regions[el.dataset.okbRegion] = el;

  // Масштаб на широких экранах (T15): Zero-блоки Тильды растут вместе с окном, виджет — вслед за ними
  let scaleRaf = 0;
  const applyScale = () => {
    scaleRaf = 0;
    const z = scaleFor(window.innerWidth);
    root.style.zoom = z === 1 ? "" : String(z);
    // Модуль равняется по окну (мини — по персиковой карточке hero, полный с T26 — по линии
    // верхнего меню: 75% ширины окна по центру окна), а не по колонке блока Тильды — колонка 11/12
    // стоит левее центра окна, а vw внутри zoom считается иначе, чем %. Отдаём в CSS ширину окна
    // и расстояние от левого края #okb до центра окна, в css-px виджета
    const win = document.documentElement.clientWidth;
    root.style.setProperty("--okb-win", `${win / z}px`);
    root.style.setProperty("--okb-shift", `${(win / 2 - root.getBoundingClientRect().left) / z}px`);
  };
  applyScale();
  window.addEventListener("resize", () => {
    if (!scaleRaf) scaleRaf = requestAnimationFrame(applyScale);
  });
  const toastEl = root.querySelector(".okb-toast");

  const store = createState();
  const track = createTracker(sendGoal);
  let mapFetchedAt = 0;

  /* ---------- рендер: регион перерисовывается только при изменении разметки ---------- */
  const lastHtml = {};
  function draw(name, html) {
    if (lastHtml[name] === html) return;
    lastHtml[name] = html;
    regions[name].innerHTML = html;
  }
  function render(state) {
    if (mini) renderMini(state);
    else renderFull(state);
    const modalHtml =
      state.ui.modal === "dates"
        ? renderDatesModal(state)
        : state.ui.modal === "guests"
          ? renderGuestsModal(state)
          : "";
    draw("modal", modalHtml);
    regions.modal.hidden = !modalHtml;
    lockPageScroll(Boolean(modalHtml));
  }
  // Открытая модалка держит страницу под собой (T18.2): на Тильде колесо, тачпад и палец листали
  // главную под календарём. overflow:hidden на html и body — десктоп; на телефонах страховка —
  // overscroll-behavior оверлея (CSS). Прежние inline-значения возвращаем при закрытии: страница
  // чужая, свои стили ей не навязываем дольше, чем открыта модалка
  let savedOverflow = null;
  function lockPageScroll(on) {
    const els = [document.documentElement, document.body];
    if (on && savedOverflow == null) {
      savedOverflow = els.map((el) => el.style.overflow);
      for (const el of els) el.style.overflow = "hidden";
    } else if (!on && savedOverflow != null) {
      els.forEach((el, i) => (el.style.overflow = savedOverflow[i]));
      savedOverflow = null;
    }
  }
  // мини (T18): только поиск; из плашек — лишь фатальная ошибка (устаревшие данные на
  // публичной главной не показываем); экраны оформления, статуса и гейта недостижимы
  function renderMini(state) {
    draw("intro", "");
    draw("search", renderSearch(state));
    draw("notice", state.error ? renderNotice(state) : "");
    draw("results", "");
  }
  function renderFull(state) {
    // Экраны: статус → оформление → поиск. На «соло»-экранах поиск скрыт (ref_6). Пока
    // state.checkout в фазе form не меняется, renderCheckout выдаёт ту же строку — draw() не
    // трогает DOM и значения полей живут.
    // denied — мягкий гейт T9: страница открыта без ?key= (тихая заглушка вместо модуля)
    const screen = state.denied
      ? "denied"
      : state.status
        ? "status"
        : state.checkout
          ? "checkout"
          : "search";
    // вступление в стиле сайта (T15): заголовок, что делать и как читать цены — над поиском
    draw("intro", screen === "search" ? renderIntro(state) : "");
    draw("search", screen === "search" ? renderSearch(state) : "");
    draw("notice", screen === "denied" ? "" : renderNotice(state));
    draw(
      "results",
      screen === "denied"
        ? `<div class="okb-hintcard"><div><h3>Страница доступна по прямой ссылке</h3>
           <p>Откройте бронирование по ссылке, которую вам прислали.</p></div></div>`
        : screen === "status"
          ? renderStatus(state)
          : screen === "checkout"
            ? renderCheckout(state)
            : state.search
              ? renderResults(state)
              : "", // до поиска всё сказано во вступлении (T15)
    );
  }
  store.subscribe(render);

  let toastTimer = null;
  function toast(text) {
    toastEl.textContent = text;
    toastEl.classList.add("okb-on");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove("okb-on"), 4500);
  }

  /* ---------- данные ---------- */
  async function loadMap() {
    const map = indexMap(await api.getMap());
    mapFetchedAt = Date.now();
    store.set({ map });
  }

  // Полёты в пути по ключу: повторный вызов (например, «Найти» во время загрузки цен)
  // не выходит впустую, а ждёт общий запрос — иначе клик по кнопке выглядел бы «зависшим»
  const lengthsInFlight = new Map();
  async function loadLengths(from, adults) {
    const key = lengthsKey(from, adults);
    const cur = store.get().lengths;
    if (cur.key === key && cur.data) return;
    if (cur.key === key && cur.loading) return lengthsInFlight.get(key);
    const flight = (async () => {
      store.set({ lengths: { key, data: null, loading: true, error: null } });
      try {
        const data = await api.getLengths(from, adults);
        if (store.get().lengths.key !== key) return; // пока грузили, выбрали другое
        store.set({ lengths: { key, data, loading: false, error: null } });
        settlePendingOut();
        // выбранный выезд мог стать недоступен (сменили гостей / изменилась шахматка);
        // длины нет в ответе (partial) — неизвестно, не сбрасываем (T23c: карточки уже из quote)
        const st = store.get();
        if (st.dates.checkin === from && st.dates.checkout) {
          const known = hasNight(data, diffNights(from, st.dates.checkout));
          const ok = candidatesFromLengths(data, adults).some((c) => c.to === st.dates.checkout);
          if (known && !ok) {
            store.set({ dates: { checkin: from, checkout: null } });
            toast("Выбранная дата выезда стала недоступна — выберите другую.");
          }
        }
        autoAdvanceToCheckouts();
      } catch (err) {
        if (store.get().lengths.key !== key) return;
        store.set({ lengths: { key, data: null, loading: false, error: err.message } });
        settlePendingOut();
      }
    })();
    lengthsInFlight.set(key, flight);
    try {
      await flight;
    } finally {
      lengthsInFlight.delete(key);
    }
  }

  // Мгновенные кандидаты выезда (/api/checkouts, SQLite): бегут параллельно loadLengths,
  // календарь подсвечивает даты по ним сразу, цены подставляются с приходом lengths.
  // Ошибку не показываем — это ускоритель, финальная правда за lengths.
  async function loadCheckouts(from, adults) {
    const key = lengthsKey(from, adults);
    if (
      store.get().checkouts.key === key &&
      (store.get().checkouts.data || store.get().checkouts.loading)
    )
      return;
    store.set({ checkouts: { key, data: null, loading: true, error: null } });
    try {
      const data = await api.getCheckouts(from, adults);
      if (store.get().checkouts.key !== key) return; // пока грузили, выбрали другое
      store.set({ checkouts: { key, data, loading: false, error: null } });
      autoAdvanceToCheckouts();
    } catch (err) {
      if (store.get().checkouts.key !== key) return;
      store.set({ checkouts: { key, data: null, loading: false, error: err.message } });
    }
  }

  // Автоперелистывание (фикс 03.09.2026): заезд выбран в конце месяца — все допустимые выезды
  // в следующем; как только кандидаты известны, показываем месяц, где они есть.
  // Только на шаге «выезд» и только в момент прихода данных/клика — ручную навигацию не трогаем.
  function autoAdvanceToCheckouts() {
    const st = store.get();
    if (st.ui.modal !== "dates" || !st.dates.checkin || st.dates.checkout) return;
    const picked = outCandidates(st);
    if (!picked?.list.length) return;
    const idx = monthIndexForCandidates(
      calendarMonths(st),
      st.ui.monthIndex,
      picked.list.map((c) => c.to),
    );
    if (idx !== st.ui.monthIndex) store.set({ ui: { ...st.ui, monthIndex: idx } });
  }

  // данные экрана оформления — способы оплаты и доп. услуги (T11) — грузятся ДО фазы form
  // одним патчем: их приход не должен перерисовать форму (значения полей живут в DOM)
  async function loadCheckoutData(c) {
    const q = {
      from: c.search.checkin,
      to: c.search.checkout,
      adults: c.search.adults,
      children: c.search.children,
      catId: c.card.catId,
      tariffId: c.card.tariffId,
    };
    const [opt, svc] = await Promise.all([
      api.getPaymentOptions(q).then(
        // токен формы (T10d): паузу перед отправкой считаем от получения ответа
        ({ options, formToken }) => ({
          options,
          optionsError: null,
          formToken: formToken ?? null,
          formTokenAt: Date.now(),
        }),
        (err) => ({ options: [], optionsError: err.message, formToken: null, formTokenAt: null }),
      ),
      api.getServices(q).then(
        ({ services }) => ({ services, servicesError: null }),
        (err) => ({ services: [], servicesError: err.message }),
      ),
    ]);
    const cur = store.get().checkout;
    if (!cur || cur.card !== c.card) return; // пока грузили, ушли с экрана
    store.set({ checkout: { ...cur, phase: "form", ...opt, ...svc } });
  }

  async function loadOrderStatus(orderToken) {
    let patch;
    try {
      patch = { data: await api.getOrder(orderToken), error: null };
    } catch (err) {
      patch = { data: null, error: err.message };
    }
    const cur = store.get().status;
    if (!cur || cur.orderToken !== orderToken) return;
    store.set({ status: { ...cur, loading: false, ...patch } });
    if (patch.data?.isPaid === true) track.paid(orderToken); // book_paid, один раз на заказ
  }

  // стартовый месяц календаря: месяц заезда, иначе первый месяц с доступным заездом
  function initialMonthIndex(state) {
    const months = calendarMonths(state);
    const target =
      state.dates.checkin ??
      [...(state.map?.byDate.keys() ?? [])].filter((d) => d >= todayIso()).sort()[0];
    if (!target) return 0;
    const idx = months.findIndex(([y, m]) =>
      target.startsWith(`${y}-${String(m + 1).padStart(2, "0")}`),
    );
    return idx >= 0 ? idx : 0;
  }
  const gotoMonthOf = (state, dateIso) => {
    const idx = calendarMonths(state).findIndex(([y, m]) =>
      dateIso.startsWith(`${y}-${String(m + 1).padStart(2, "0")}`),
    );
    return idx >= 0 ? idx : state.ui.monthIndex;
  };

  // События календаря (T37) — в лог сервера для недельного отчёта: тап по выезду и по
  // недоступной дате. checkinAt — момент выбора заезда (мс от него видны в логе; у дат из
  // ссылки мини-модуля его нет)
  let checkinAt = null;
  function calEvent(e, date, { src, reason, change } = {}) {
    const st = store.get();
    const since = checkinAt == null ? null : Date.now() - checkinAt;
    api.calEvent(calEventParams({ e, state: st, date, src, since, change, reason }));
  }
  // Гость тапнул выезд, пока цены были в пути (заметка «Проверяем выезд…»): решаем, как только
  // цены пришли или упали
  // «Проверяем выезд…» открываем, только если цены не пришли за PENDING_NOTE_DELAY_MS — та же
  // заметка (по ссылке) ещё ждёт: иначе её уже заменили выезд, причина или новый тап
  function revealPendingLater() {
    const w = store.get().ui.why;
    if (!w?.pending || w.shown) return;
    setTimeout(() => {
      const ui = store.get().ui;
      if (ui.why === w) store.set({ ui: { ...ui, why: { ...w, shown: true } } });
    }, PENDING_NOTE_DELAY_MS);
  }
  function settlePendingOut() {
    const st = store.get();
    const res = resolvePending(st);
    if (!res) return;
    store.set({
      dates: { checkin: st.dates.checkin, checkout: res.checkout },
      ui: { ...st.ui, why: res.why },
    });
    calEvent(res.event.e, res.event.date, res.event);
  }

  /* ---------- действия ---------- */
  // Стрелки карусели (T9.5e): листаем скролл-ленту без перерисовки — точки обновит слушатель
  // scroll ниже; по кругу, у краёв не упираемся
  function slideShots(el, dir) {
    const shots = el.closest(".okb-carousel")?.querySelector("[data-okb-shots]");
    if (!shots || !shots.children.length) return;
    const w = shots.clientWidth || 1;
    const n = shots.children.length;
    const next = (Math.round(shots.scrollLeft / w) + dir + n) % n;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    shots.scrollTo({ left: next * w, behavior: reduce ? "auto" : "smooth" });
  }

  const actions = {
    "shot-prev": (el) => slideShots(el, -1),
    "shot-next": (el) => slideShots(el, 1),
    "open-dates"() {
      const st = store.get();
      store.set({
        ui: {
          ...st.ui,
          modal: "dates",
          altFor: null,
          why: null,
          calMode: null, // шаг по выбранным датам: без заезда — заезд, с ним — выезд (T37)
          monthIndex: initialMonthIndex(st),
        },
      });
      if (Date.now() - mapFetchedAt > 60_000) loadMap().catch(() => {});
      // кандидаты выезда нужны и при выбранных обеих датах — тап справа меняет выезд (T37)
      if (st.dates.checkin) {
        loadCheckouts(st.dates.checkin, st.guests.adults);
        loadLengths(st.dates.checkin, st.guests.adults);
        autoAdvanceToCheckouts(); // данные могли быть уже в кэше — событие прихода не случится
      }
    },
    "open-guests"() {
      store.set({ ui: { ...store.get().ui, modal: "guests" } });
    },
    "close-modal"() {
      store.set({ ui: { ...store.get().ui, modal: null, altFor: null, why: null } });
    },
    "confirm-dates"() {
      actions["close-modal"]();
    },
    "cal-prev"() {
      const ui = store.get().ui;
      store.set({
        ui: { ...ui, monthIndex: Math.max(0, ui.monthIndex - 1), altFor: null, why: null },
      });
    },
    "cal-next"() {
      const st = store.get();
      const max = calendarMonths(st).length - 1;
      store.set({
        ui: { ...st.ui, monthIndex: Math.min(max, st.ui.monthIndex + 1), altFor: null, why: null },
      });
    },
    "pick-in"(el) {
      const st = store.get();
      const date = el.dataset.iso;
      checkinAt = Date.now();
      store.set({
        dates: { checkin: date, checkout: null },
        ui: { ...st.ui, altFor: null, why: null, calMode: null, monthIndex: gotoMonthOf(st, date) },
      });
      loadCheckouts(date, st.guests.adults); // мгновенная подсветка — параллельно ценам
      loadLengths(date, st.guests.adults);
      autoAdvanceToCheckouts(); // кандидаты по карте есть сразу — листаем к месяцу выездов
    },
    // выезд — и первый, и смена уже выбранного (T37)
    "pick-out"(el) {
      const st = store.get();
      const date = el.dataset.iso;
      const ev = pickOutEvent(st, date); // до store.set — он мержит в тот же объект
      store.set({
        dates: { ...st.dates, checkout: date },
        ui: { ...st.ui, altFor: null, why: null },
      });
      calEvent(ev.e, ev.date, ev);
    },
    // тап левее заезда — перенос заезда; выезд остаётся, если с новой даты он допустим (T37)
    "move-in"(el) {
      const st = store.get();
      const date = el.dataset.iso;
      const { dates, why } = moveCheckin(st, date);
      checkinAt = Date.now();
      // после нового заезда — снова шаг выезда (чип «Выезд»)
      store.set({
        dates,
        ui: { ...st.ui, altFor: null, why, calMode: null, monthIndex: gotoMonthOf(st, date) },
      });
      loadCheckouts(date, st.guests.adults);
      loadLengths(date, st.guests.adults);
      // цены нового заезда уже в кэше — проверка выезда («Проверяем…») решается сразу
      settlePendingOut();
      revealPendingLater();
      autoAdvanceToCheckouts();
    },
    // недоступный выезд: причина под календарём, заезд и выезд не трогаем (T37)
    why(el) {
      const st = store.get();
      const date = el.dataset.iso;
      const res = blockedTap(st, date);
      store.set({ ui: { ...st.ui, altFor: null, why: res.why } });
      revealPendingLater();
      calEvent("blocked", date, { src: res.src, reason: res.why.code });
    },
    "cal-reset"() {
      checkinAt = null;
      store.set({
        dates: { checkin: null, checkout: null },
        ui: { ...store.get().ui, altFor: null, why: null, calMode: null },
      });
    },
    // чипы над календарём (T37): «Заезд» — следующий тап ставит новый заезд, «Выезд» — шаг выезда
    "mode-in"() {
      store.set({ ui: { ...store.get().ui, calMode: "in", altFor: null, why: null } });
    },
    "mode-out"() {
      const st = store.get();
      if (!st.dates.checkin) return;
      store.set({ ui: { ...st.ui, calMode: null, altFor: null, why: null } });
      autoAdvanceToCheckouts();
    },
    alt(el) {
      store.set({ ui: { ...store.get().ui, altFor: el.dataset.iso, why: null } });
    },
    "ad-plus"() {
      changeGuests(+1, 0);
    },
    "ad-minus"() {
      changeGuests(-1, 0);
    },
    "ch-plus"() {
      changeGuests(0, +1);
    },
    "ch-minus"() {
      changeGuests(0, -1);
    },
    async find() {
      const st = store.get();
      const { checkin, checkout } = st.dates;
      if (st.ui.finding) return;
      // «Найти номер» без дат не выключена (T25, с T26 и в полном модуле) — вместо серой
      // кнопки открываем календарь
      if (!(checkin && checkout)) {
        actions["open-dates"]();
        return;
      }
      if (mini) {
        // мини (T18): не ищем на месте — уводим на страницу полного модуля, поиск запустит он.
        // «Ищем…» держим до смены страницы; возврат «Назад» из bfcache сбросит его (pageshow ниже)
        store.set({ ui: { ...st.ui, finding: true } });
        location.assign(
          bookingPageUrl(
            miniPage,
            {
              key: st.config?.widgetKey ?? null,
              checkin,
              checkout,
              adults: st.guests.adults,
              children: st.guests.children,
            },
            location.href,
          ),
        );
        return;
      }
      const adults = st.guests.adults;
      const L = diffNights(checkin, checkout);
      // карточки — из /api/quote (T23c): один запрос с высшим приоритетом, пачка цен длин для
      // календаря догружается следом и «Найти» её не ждёт; «Ищем…» на кнопке, пока ждём ответ
      store.set({ ui: { ...st.ui, finding: true } });
      let data = null;
      let refusal = null;
      try {
        data = await runSearch({
          from: checkin,
          to: checkout,
          adults,
          children: st.guests.children,
          L,
          lengths: st.lengths,
          api,
          loadLengths,
          // повтор после таймаута/обрыва (T24): ui читаем свежим из store — за 12 с гость мог
          // открыть модалку или сменить месяц, устаревший st.ui закрыл бы её
          onRetry: () => {
            store.set({ ui: { ...store.get().ui, finding: "retry" } });
            toast("Связь прервалась, пробуем ещё раз");
          },
        });
      } catch (err) {
        // отказ правил — честный пустой результат с причиной в блоке; остальное — тост
        if (isRulesRefusal(err)) {
          data = refusedSearchData(checkin, checkout, L);
          refusal = err.message;
        } else toast(err.message);
      } finally {
        store.set({ ui: { ...store.get().ui, finding: false } });
      }
      if (!data) return;
      const cur = store.get();
      // пока искали, даты сменили (или валидация lengths сбросила выезд) — снимок устарел
      if (cur.dates.checkin !== checkin || cur.dates.checkout !== checkout) return;
      store.set({
        search: {
          checkin,
          checkout,
          adults,
          children: st.guests.children,
          L,
          lengthsData: data,
          refusal,
        },
      });
      regions.results.scrollIntoView?.({ behavior: "smooth", block: "start" });
    },
    // «Забронировать» на карточке → экран оформления (T6)
    book(el) {
      const st = store.get();
      const s = st.search;
      if (!s) return;
      const catId = Number(el.dataset.cat);
      const card = cardsFromLengths(s.lengthsData, st.map?.cats, s.L, s.adults).find(
        (c) => c.catId === catId,
      );
      if (!card) return;
      const checkout = {
        card,
        search: s,
        phase: "loading",
        options: null,
        optionsError: null,
        services: null,
        servicesError: null,
        result: null,
      };
      store.set({ checkout });
      root.scrollIntoView?.({ behavior: "smooth", block: "start" });
      loadCheckoutData(checkout);
    },
    // степпер количества услуги (T11): только DOM, без store.set — форма не перерисовывается
    "svc-plus": (el) => stepService(el, 1),
    "svc-minus": (el) => stepService(el, -1),
    "checkout-back"() {
      store.set({ checkout: null });
    },
    "co-reload-options"() {
      const c = store.get().checkout;
      if (!c) return;
      store.set({ checkout: { ...c, phase: "loading", optionsError: null, servicesError: null } });
      loadCheckoutData(c);
    },
    "status-back"() {
      try {
        history.replaceState(null, "", location.pathname); // чистим query возврата с оплаты
      } catch {
        /* песочница без history — просто уходим с экрана */
      }
      store.set({ status: null });
    },
  };

  /* ---------- форма оформления: значения живут в DOM, ошибки — точечным DOM ---------- */
  function formValues(form) {
    const fd = new FormData(form);
    const s = (k) => String(fd.get(k) ?? "");
    return {
      firstName: s("firstName"),
      middleName: s("middleName"),
      lastName: s("lastName"),
      phone: s("phone"),
      email: s("email"),
      comment: s("comment"),
      okb_extra: s("okb_extra"), // приманка для ботов (T10d), у людей пустая
      agree: form.elements.agree?.checked === true,
      paymentMethodID: Number(fd.get("payment")) || null,
      services: readServices(form),
    };
  }

  // Выбранные доп. услуги (T11): чекбокс svc-<id>, количество — скрытое поле svc-<id>-count,
  // цена и потолок — data-атрибуты строки (единственный источник правды, обновляется при 409)
  function readServices(form) {
    const out = [];
    for (const row of form.querySelectorAll("[data-okb-svc]")) {
      const id = Number(row.dataset.okbSvc);
      if (form.elements[`svc-${id}`]?.checked !== true) continue;
      const countEl = form.elements[`svc-${id}-count`];
      const count = countEl ? Math.max(1, Number(countEl.value) || 1) : 1;
      out.push({ id, count, price: Number(row.dataset.okbSvcPrice) || 0 });
    }
    return out;
  }

  function stepService(el, dir) {
    const row = el.closest("[data-okb-svc]");
    const form = el.closest("form");
    const input = row && form ? form.elements[`svc-${row.dataset.okbSvc}-count`] : null;
    if (!input) return;
    const max = Number(row.dataset.okbSvcMax) || 1;
    input.value = String(Math.min(max, Math.max(1, (Number(input.value) || 1) + dir)));
    recalcServices(form);
  }

  // Пересчёт услуг — только DOM: формула и сумма строки, показ степпера у отмеченных,
  // затем суммы карточки-итога. Актуальные проживание/предоплата после 409 — в dataset формы.
  function recalcServices(form) {
    let sum = 0;
    for (const row of form.querySelectorAll("[data-okb-svc]")) {
      const id = Number(row.dataset.okbSvc);
      const checked = form.elements[`svc-${id}`]?.checked === true;
      const price = Number(row.dataset.okbSvcPrice) || 0;
      const countEl = form.elements[`svc-${id}-count`];
      const count = countEl ? Math.max(1, Number(countEl.value) || 1) : 1;
      const qty = row.querySelector(".okb-svcqty");
      if (qty) {
        qty.hidden = !checked;
        const max = Number(row.dataset.okbSvcMax) || 1;
        const minus = qty.querySelector('[data-okb="svc-minus"]');
        const plus = qty.querySelector('[data-okb="svc-plus"]');
        if (minus) minus.disabled = count <= 1;
        if (plus) plus.disabled = count >= max;
        const perDay = row.dataset.okbSvcPerday === "1";
        const f = qty.querySelector("[data-okb-svc-formula]");
        if (f) f.textContent = formulaLabel(price, count, perDay);
        const l = qty.querySelector("[data-okb-svc-line]");
        if (l) l.textContent = fmtRub(price * count);
        const n = qty.querySelector("[data-okb-svc-count]");
        if (n) n.textContent = String(count);
      }
      if (checked) sum += price * count;
    }
    form.dataset.okbServicesTotal = String(sum);
    const c = store.get().checkout;
    if (!c) return;
    const total = Number(form.dataset.okbTotal) || c.card.total;
    const prepay =
      form.dataset.okbPrepay != null ? Number(form.dataset.okbPrepay) : c.card.prepaymentRub;
    updateSummarySums(total, prepay, sum);
  }

  // 409 price_changed по услугам: свежие цены за единицу — в data-атрибуты и подписи строк
  function patchServicePrices(form, services) {
    for (const s of services) {
      const row = form.querySelector(`[data-okb-svc="${s.id}"]`);
      if (!row) continue;
      row.dataset.okbSvcPrice = String(s.price);
      const label = row.querySelector(".okb-svcprice");
      if (label)
        label.textContent = priceLabel({
          price: s.price,
          perDay: row.dataset.okbSvcPerday === "1",
        });
    }
  }

  // 409 services_gone: услуга исчезла из каталога — снимаем выбор и гасим строку
  function markServiceGone(form, serviceId) {
    const row = form.querySelector(`[data-okb-svc="${serviceId}"]`);
    const box = row ? form.elements[`svc-${serviceId}`] : null;
    if (!row || !box) return;
    box.checked = false;
    box.disabled = true;
    row.classList.add("okb-svc-gone");
    const label = row.querySelector(".okb-svcprice");
    if (label) label.textContent = "больше недоступна";
  }

  function showFieldErrors(form, errors) {
    for (const wrap of form.querySelectorAll("[data-okb-field]")) {
      const err = errors[wrap.dataset.okbField];
      wrap.classList.toggle("okb-invalid", Boolean(err));
      const box = wrap.querySelector(".okb-err");
      if (box) {
        box.textContent = err ?? "";
        box.hidden = !err;
      }
    }
  }

  // Живая валидация: до первой отправки — только состояние кнопки, после — и тексты ошибок
  function refreshCheckoutForm(form) {
    recalcServices(form);
    const values = formValues(form);
    const { ok, errors } = validateGuestForm(values);
    if (form.dataset.okbTried) showFieldErrors(form, errors);
    const btn = form.querySelector(".okb-submitbtn");
    if (btn && !form.dataset.okbSending) {
      btn.disabled =
        store.get().config?.bookingEnabled !== true || !ok || values.paymentMethodID == null;
    }
  }

  // Попытка брони на форму оформления (T34): { key, fp } — ключ и отпечаток последней отправки
  const bookAttempts = new WeakMap();

  async function submitCheckout(form) {
    form.dataset.okbTried = "1";
    const c = store.get().checkout;
    if (!c || store.get().config?.bookingEnabled !== true || form.dataset.okbSending) return;
    const values = formValues(form);
    const { ok, errors } = validateGuestForm(values);
    showFieldErrors(form, errors);
    if (!ok || values.paymentMethodID == null) return;

    const btn = form.querySelector(".okb-submitbtn");
    const errBox = form.querySelector(".okb-coerror");
    const waitBox = form.querySelector(".okb-cowait");
    errBox.hidden = true;
    form.dataset.okbSending = "1";
    btn.disabled = true;
    const btnText = btn.textContent;
    // ожидание (T35): кнопка пульсирует, текст и подсказка — по стадиям с момента нажатия
    // («Отправляем…», с 5 с «Создаём бронь…», с 20 с «дольше обычного», при автоповторе — проверка)
    const sentAt = Date.now();
    let retrying = false;
    const showStage = () => {
      const stage = bookWaitStage(Date.now() - sentAt, { retrying });
      btn.textContent = stage.button;
      if (waitBox) {
        waitBox.textContent = stage.hint;
        waitBox.hidden = !stage.hint;
      }
    };
    btn.classList.add("okb-busy");
    showStage();
    const stageTimer = setInterval(showStage, 1000);
    try {
      // ботозащита (T10d): сервер принимает токен формы не раньше 4 с после выдачи — доживаем
      // остаток (обычно 0: поля заполняют дольше), кнопка уже показывает «Отправляем…»
      const wait = formSubmitDelay(c.formTokenAt, Date.now());
      if (wait > 0) await new Promise((resolve) => setTimeout(resolve, wait));
      if (store.get().checkout?.card !== c.card) return; // пока ждали, ушли с экрана
      // актуальная цена после 409 price_changed живёт в data-okb-total, не в card —
      // так render() не видит изменений и не перерисовывает форму
      const expectedTotal = Number(form.dataset.okbTotal) || c.card.total;
      const payload = buildBookPayload({
        search: c.search,
        card: { ...c.card, total: expectedTotal },
        values,
        paymentMethodID: values.paymentMethodID,
        formToken: c.formToken,
      });
      // ключ попытки (T34) привязан к форме: повтор той же формы после обрыва — тот же ключ.
      // В памяти, не в data-атрибутах: отпечаток содержит контакты гостя, а DOM чужой страницы
      // читают сторонние скрипты (Вебвизор)
      const attempt = nextBookAttempt(bookAttempts.get(form), bookFingerprint(payload));
      bookAttempts.set(form, attempt);
      const result = await bookWithRetry(
        api,
        { ...payload, attemptKey: attempt.key },
        {
          onRetry: () => {
            retrying = true;
            showStage();
          },
        },
      );
      // цель Метрики — бронь создана (T34), даже если гость уже ушёл с экрана
      track.created(result.orderToken ?? result.bookingCode);
      const cur = store.get().checkout;
      if (!cur || cur.card !== c.card) return;
      store.set({ checkout: { ...cur, phase: "done", result } }); // полей больше нет — редрав легален
    } catch (err) {
      delete form.dataset.okbSending;
      btn.textContent = btnText;
      btn.disabled = false;
      // исход известен (наш ответ с code) — следующая отправка будет новой попыткой
      if (!keepBookAttempt(err)) bookAttempts.delete(form);
      const body = err.body;
      if (body?.code === "price_changed") {
        if (body.total != null) form.dataset.okbTotal = String(body.total);
        if (body.prepaymentRub != null) form.dataset.okbPrepay = String(body.prepaymentRub);
        if (Array.isArray(body.services)) patchServicePrices(form, body.services);
        recalcServices(form);
      } else if (body?.code === "services_gone" && body.serviceId != null) {
        markServiceGone(form, body.serviceId);
        recalcServices(form);
      }
      errBox.textContent = err.message;
      errBox.hidden = false;
    } finally {
      clearInterval(stageTimer);
      btn.classList.remove("okb-busy");
      if (waitBox) waitBox.hidden = true;
    }
  }

  // точечное обновление сумм карточки-итога (409 price_changed, выбор услуг) — без store.set
  // и редрава. Услуги входят в «Итого» и остаток, но не в «К оплате сейчас» (T11, D3).
  function updateSummarySums(total, prepaymentRub, servicesTotal = 0) {
    const set = (name, value) => {
      const el = regions.results.querySelector(`[data-okb-sum="${name}"]`);
      if (el && value != null) el.textContent = fmtRub(value);
    };
    set("total", total);
    set("services", servicesTotal);
    const line = regions.results.querySelector("[data-okb-svcline]");
    if (line) line.hidden = !(servicesTotal > 0);
    set("grand", total + servicesTotal);
    set("prepay", prepaymentRub);
    set("rest", restAtCheckin(total, prepaymentRub, servicesTotal));
  }

  function changeGuests(dAdults, dChildren) {
    const st = store.get();
    const cap = st.config?.guests?.maxRoomCapacity ?? 6;
    const adults = Math.min(cap, Math.max(1, st.guests.adults + dAdults));
    const children = Math.min(
      Math.max(0, cap - adults),
      Math.max(0, st.guests.children + dChildren),
    );
    store.set({ guests: { adults, children }, ui: { ...st.ui, why: null } });
    // цены и вместимость зависят от взрослых: перезапросить оба источника для заезда
    if (adults !== st.guests.adults && st.dates.checkin) {
      loadCheckouts(st.dates.checkin, adults);
      loadLengths(st.dates.checkin, adults);
    }
  }

  root.addEventListener("click", (e) => {
    const el = e.target.closest?.("[data-okb]");
    if (el && actions[el.dataset.okb]) {
      const goal = GOALS[el.dataset.okb];
      if (goal) track.goal(goal);
      actions[el.dataset.okb](el);
      return;
    }
    if (e.target === regions.modal) actions["close-modal"]();
  });
  // форма оформления: submit + живая валидация (input не ловит радио/чекбоксы во всех браузерах —
  // подстраховываемся change)
  root.addEventListener("submit", (e) => {
    const form = e.target.closest?.('form[data-okb-form="checkout"]');
    if (!form) return;
    e.preventDefault();
    submitCheckout(form);
  });
  for (const type of ["input", "change"]) {
    root.addEventListener(type, (e) => {
      const form = e.target.closest?.('form[data-okb-form="checkout"]');
      if (form) refreshCheckoutForm(form);
    });
  }
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && store.get().ui.modal) actions["close-modal"]();
  });
  // мини (T18): «Назад» со страницы модуля — Safari/Chrome поднимают страницу из bfcache
  // с застывшим «Ищем…»; возвращаем кнопку
  if (mini)
    window.addEventListener("pageshow", (e) => {
      const ui = store.get().ui;
      if (e.persisted && ui.finding) store.set({ ui: { ...ui, finding: false } });
    });
  // точки карусели: скролл не всплывает — ловим в capture-фазе
  root.addEventListener(
    "scroll",
    (e) => {
      const shots = e.target;
      if (!shots.matches?.("[data-okb-shots]")) return;
      const i = Math.round(shots.scrollLeft / shots.clientWidth);
      const dots = shots.parentElement.querySelectorAll(".okb-dots i");
      dots.forEach((d, j) => d.classList.toggle("okb-on", j === i));
    },
    true,
  );

  /* ---------- загрузка ---------- */
  // возврат с оплаты: в query есть orderToken → сразу экран статуса (работает на /status и /demo);
  // мини (T18) экрана статуса не имеет — случайный ?okb_order= на главной не трогаем
  const statusFromUrl = mini ? null : statusParamsFromLocation(location.search);
  if (statusFromUrl) {
    store.set({
      status: { orderToken: statusFromUrl.orderToken, loading: true, data: null, error: null },
    });
    loadOrderStatus(statusFromUrl.orderToken);
  }
  render(store.get());
  Promise.all([api.getConfig(), api.getMap()])
    .then(([config, mapJson]) => {
      // мягкий гейт T9 — до выкладки данных; возврат с оплаты (statusFromUrl) проходит всегда.
      // Мини (T18) гейтом не закрыт: это публичная строка поиска, ключ она сама подставит в ссылку
      const allowed =
        mini ||
        gateAllows({
          widgetKey: config.widgetKey,
          key: keyFromLocation(location.search),
          hasStatus: Boolean(statusFromUrl),
        });
      if (!allowed) {
        track.init(null);
        store.set({ denied: true });
        return;
      }
      mapFetchedAt = Date.now();
      store.set({
        config,
        map: indexMap(mapJson),
        guests: { adults: config.guests?.defaultAdults ?? 2, children: 0 },
      });
      if (config.metrikaId) {
        ensureCounter(config.metrikaId);
        track.init(config.metrikaId);
      } else {
        track.init(null);
      }
      // автопоиск (T18): пришли с мини-модуля главной — даты и гости в query, ищем без клика.
      // Строго после патча с config: find() и степпер читают вместимость оттуда. Отказ правил
      // покажет блок результатов, ошибку сервера — тост из find; недоступный по ценам длин выезд
      // сбросит валидация в loadLengths, когда фоновая пачка долетит
      const pre =
        mini || statusFromUrl
          ? null
          : searchFromLocation(location.search, {
              today: todayIso(),
              cap: config.guests?.maxRoomCapacity ?? 6,
              defaultAdults: config.guests?.defaultAdults ?? 2,
            });
      if (pre) {
        store.set({
          dates: { checkin: pre.checkin, checkout: pre.checkout },
          guests: { adults: pre.adults, children: pre.children },
        });
        actions.find();
      }
    })
    .catch((err) => {
      track.init(null);
      store.set({ error: err.message });
    });
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
